/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, of, Subject } from 'rxjs';
import { GenericDialogData } from 'src/app/modules/projects2/projects2.interfaces';
import * as appStrings from '@touchplan/strings';

@Component({
	selector: 'generic-dialog',
	templateUrl: './generic-dialog.component.html',
	styleUrls: ['./generic-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})

/**
 * Generic Material dialog with customizable title, content and yes/no button texts
 * Use afterClosed() observable to response to yes click, Yes emits true, and No emits false, e.g. where this dialog is opened from:
 * dialogRef.afterClosed().pipe( filter(data => data), takeUntil(this.destroy$) ).subscribe( => { ... });
 */
export class GenericDialogComponent implements OnInit, OnDestroy {
	public formGroup: UntypedFormGroup;
	public contentLine$: Observable<string[]>;
	protected destroy$: Subject<void>;

	/**
	 * Displays an information dialog, with the given title, content, and primary button text
	 */
	public static displayInformationDialog(
		dialog: MatDialog,
		title: string,
		content: string,
		yesButtonText: string
	): MatDialogRef<GenericDialogComponent, boolean> {
		return dialog.open(GenericDialogComponent, {
			data: {
				title,
				content,
				yesButtonText,
			},
			disableClose: true,
		});
	}

	/**
	 * Displays a confirmation dialog, with the given title, yes button text and no button text
	 */
	public static displayConfirmationDialog(
		dialog: MatDialog,
		title: string,
		yesButtonText: string,
		noButtonText: string
	): MatDialogRef<GenericDialogComponent, boolean> {
		return dialog.open(GenericDialogComponent, {
			data: {
				title,
				yesButtonText,
				noButtonText,
			},
			disableClose: true,
		});
	}

	/**
	 * Displays a generic "Are you sure?" dialog, with "YES" and "NO" buttons
	 */
	public static displayGenericConfirmationDialog(
		dialog: MatDialog
	): MatDialogRef<GenericDialogComponent, boolean> {
		return GenericDialogComponent.displayConfirmationDialog(
			dialog,
			appStrings.DELETE_DIALOG_TEXT,
			appStrings.DELETE_DIALOG_YES,
			appStrings.DELETE_DIALOG_NO
		);
	}

	/**
	 * Update the content on the dialog.
	 */
	public updateContent(content: string | string[]) {
		this.formGroup.patchValue({ content });
		this.contentLine$ = of(typeof content === 'string' ? [content] : content);
	}

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: GenericDialogData,
		public dialogRef: MatDialogRef<GenericDialogComponent>,
		protected formBuilder: UntypedFormBuilder
	) {
		this.destroy$ = new Subject<void>();
	}

	public ngOnInit() {
		if (!this.data) {
			this.data = {} as GenericDialogData;
		}
		// TP-2491
		// In the nullish / uninitialized button color cases, make them default to the 'primary' color:
		this.data.yesButtonColor ??= 'primary';
		this.data.noButtonColor ??= 'primary';

		// TP-2490
		// "this.data.content" can be nullish, a single "string" or a "string[]".
		// Whatever the case, make "this.contentLine$" be an "Observable<string[]>"
		this.contentLine$ = of(
			!this.data.content
				? []
				: typeof this.data.content === 'string'
				? [this.data.content]
				: this.data.content
		);

		this.formGroup = this.formBuilder.group({
			title: [this.data.title],
			content: [this.data.content],
			yesButtonText: [this.data.yesButtonText],
			yesButtonColor: [this.data.yesButtonColor],
			noButtonText: [this.data.noButtonText],
			noButtonColor: [this.data.noButtonColor],
		});
	}

	public ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public onYes(): void {
		this.dialogRef.close(true);
	}

	public onNo(): void {
		this.dialogRef.close(false);
	}
}
